/**
 * Dealer profile view models (Dealer Profile M2).
 */
import {
  DEALER_PROFILE_CONTACT_COPY_ES,
  DEALER_PROFILE_FALLBACK_COPY,
  DEALER_PROFILE_HERO_TRUST_NOTE_ES,
  DEALER_PROFILE_INVENTORY_LABELS_ES,
  DEALER_PROFILE_PAGE_STATE,
  DEALER_PROFILE_TRUST_NOTE_ES,
  DEALER_PROFILE_TRUST_PANEL_CONTACT_MESSAGES_ES,
  DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES,
  DEALER_PROFILE_TRUST_PANEL_VERIFICATION_EXPLANATIONS_ES,
  DEALER_PROFILE_TRUST_PANEL_WHAT_REVIEWED_ES,
  DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES,
  DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES,
  DEALER_PROFILE_VERIFICATION_DESCRIPTIONS_ES,
  DEALER_PROFILE_VERIFICATION_LABELS_ES,
  DEALER_PROFILE_VERIFICATION_STATUS,
} from "../../constants/dealerProfileConstants";
import { buildDealerPhoneHref, buildDealerWhatsappUrl } from "./dealerContact";
import { getContactDisabledLabel, toLegacyContactDisabledReason } from "./dealerContactRules";
import { getPublicDealerInventoryListings } from "./dealerInventoryEligibility";
import { normalizeDealerProfile } from "./normalizeDealerProfile";
import { getDealerProfilePublicState } from "./dealerPublicState";
import { buildDealerProfileSeo, getDealerProfileUnavailableMetadata } from "./dealerProfileSeo";

function verificationLabel(status) {
  return DEALER_PROFILE_VERIFICATION_LABELS_ES[status] || DEALER_PROFILE_FALLBACK_COPY.verification;
}

function verificationDescription(status) {
  return (
    DEALER_PROFILE_VERIFICATION_DESCRIPTIONS_ES[status] ||
    DEALER_PROFILE_VERIFICATION_DESCRIPTIONS_ES.unknown
  );
}

function isNormalizedDealerProfile(dealer) {
  return (
    dealer &&
    dealer.publicStatus != null &&
    dealer.verificationStatus != null &&
    Object.prototype.hasOwnProperty.call(dealer, "canContact")
  );
}

function resolveDealerProfile(dealerInput, options = {}) {
  return isNormalizedDealerProfile(dealerInput)
    ? dealerInput
    : normalizeDealerProfile(dealerInput, { ...options, listing: options.listing });
}

export function formatDealerInventoryAvailabilityLabel(count) {
  if (count == null || Number.isNaN(Number(count))) {
    return DEALER_PROFILE_INVENTORY_LABELS_ES.unknown;
  }
  const value = Number(count);
  if (value <= 0) return DEALER_PROFILE_INVENTORY_LABELS_ES.empty;
  if (value === 1) return DEALER_PROFILE_INVENTORY_LABELS_ES.one;
  return DEALER_PROFILE_INVENTORY_LABELS_ES.many(value);
}

export function formatDealerLastInventoryLabel(isoDate) {
  if (!isoDate) return null;
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return null;
  const days = Math.round((Date.now() - ts) / 86_400_000);
  if (days <= 2) return "Inventario actualizado recientemente";
  return `Última actualización: ${new Date(isoDate).toLocaleDateString("es-CO")}`;
}

export function getDealerProfileHeroTrustNote(dealerTrust) {
  if (dealerTrust?.isVerified) return DEALER_PROFILE_HERO_TRUST_NOTE_ES.verified;
  return DEALER_PROFILE_HERO_TRUST_NOTE_ES.default;
}

function resolveVerificationStatusKey(dealerTrust) {
  const status = dealerTrust?.verificationStatus;
  if (status && DEALER_PROFILE_TRUST_PANEL_VERIFICATION_EXPLANATIONS_ES[status]) return status;
  return "unknown";
}

export function getDealerTrustPanelVerificationExplanation(dealerTrust) {
  const key = resolveVerificationStatusKey(dealerTrust);
  return DEALER_PROFILE_TRUST_PANEL_VERIFICATION_EXPLANATIONS_ES[key];
}

export function getDealerTrustPanelWhatReviewedCopy(dealerTrust) {
  const key = resolveVerificationStatusKey(dealerTrust);
  return DEALER_PROFILE_TRUST_PANEL_WHAT_REVIEWED_ES[key];
}

export function getDealerTrustPanelInventoryMessage(inventorySummary, publicState) {
  if (publicState?.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY) {
    return DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES.empty;
  }
  const count = inventorySummary?.activeInventoryCount;
  if (count == null || Number.isNaN(Number(count))) {
    return DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES.unknown;
  }
  const value = Number(count);
  if (value <= 0) return DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES.empty;
  if (value === 1) return DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES.one;
  return DEALER_PROFILE_TRUST_PANEL_INVENTORY_MESSAGES_ES.many(value);
}

export function getDealerTrustPanelContactMessage(dealerContact, publicState) {
  const contactLimited =
    publicState?.state === DEALER_PROFILE_PAGE_STATE.PUBLIC_CONTACT_LIMITED ||
    !dealerContact?.canContact;
  if (contactLimited) {
    return DEALER_PROFILE_TRUST_PANEL_CONTACT_MESSAGES_ES.unavailable;
  }
  return DEALER_PROFILE_TRUST_PANEL_CONTACT_MESSAGES_ES.available;
}

/**
 * @param {{
 *   dealerTrust?: object,
 *   dealerContact?: object,
 *   inventorySummary?: object,
 * }} input
 */
export function buildDealerTrustPanelSignals({ dealerTrust, dealerContact, inventorySummary } = {}) {
  const locationLabel = dealerTrust?.locationLabel;
  const hasLocation =
    locationLabel && locationLabel !== DEALER_PROFILE_FALLBACK_COPY.location;

  let contactValue = DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES.contact;
  if (dealerContact?.canContact && dealerContact?.whatsappHref) {
    contactValue = DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES.contactWhatsapp;
  } else if (dealerContact?.canContact && dealerContact?.phoneHref) {
    contactValue = DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES.contactPhone;
  }

  const activityValue =
    inventorySummary?.lastUpdatedLabel ||
    (dealerTrust?.responseTimeLabel ? `Respuesta: ${dealerTrust.responseTimeLabel}` : null) ||
    DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES.activity;

  return [
    {
      key: "verification",
      label: DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES.verification,
      value: dealerTrust?.verificationLabel || DEALER_PROFILE_FALLBACK_COPY.verification,
    },
    {
      key: "inventory",
      label: DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES.inventory,
      value:
        inventorySummary?.inventoryAvailabilityLabel ||
        DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES.inventory,
    },
    {
      key: "contact",
      label: DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES.contact,
      value: contactValue,
    },
    {
      key: "location",
      label: DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES.location,
      value: hasLocation ? locationLabel : DEALER_PROFILE_TRUST_SIGNAL_FALLBACKS_ES.location,
    },
    {
      key: "activity",
      label: DEALER_PROFILE_TRUST_SIGNAL_LABELS_ES.activity,
      value: activityValue,
    },
  ];
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|object|null|undefined} dealerInput
 * @param {{ slug?: string, listing?: object|null }} [options]
 */
export function toDealerTrustViewModel(dealerInput, options = {}) {
  const dealer = resolveDealerProfile(dealerInput, options);
  const contactDisabledReason = dealer.contactDisabledReason;
  const legacyReason = toLegacyContactDisabledReason(contactDisabledReason);

  return {
    dealerId: dealer.id,
    dealerSlug: dealer.slug,
    dealerName: dealer.displayName || dealer.name,
    dealerLogoUrl: dealer.logoUrl,
    profileHref: dealer.profileHref,
    locationLabel: dealer.locationLabel || DEALER_PROFILE_FALLBACK_COPY.location,
    verificationStatus: dealer.verificationStatus,
    verificationLabel: verificationLabel(dealer.verificationStatus),
    verificationDescription: verificationDescription(dealer.verificationStatus),
    isVerified: dealer.isVerified,
    trustBadges: dealer.trustBadges || [],
    trustFlags: dealer.trustFlags || [],
    responseTimeLabel: dealer.responseTimeLabel,
    lastActiveLabel: dealer.lastActiveAt || null,
    inventoryCountLabel:
      dealer.activeInventoryCount != null
        ? `${dealer.activeInventoryCount} vehículos`
        : DEALER_PROFILE_FALLBACK_COPY.inventoryCount,
    canContact: dealer.canContact,
    contactDisabledReason: legacyReason || contactDisabledReason,
    contactDisabledLabel: getContactDisabledLabel(contactDisabledReason),
    showVerificationBadge:
      dealer.isVerified ||
      dealer.verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.PENDING,
  };
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|object|null|undefined} dealerInput
 * @param {{ slug?: string, listing?: object|null, detailUrl?: string, message?: string }} [options]
 */
export function toDealerContactViewModel(dealerInput, options = {}) {
  const dealer = resolveDealerProfile(dealerInput, options);

  const legacyReason = toLegacyContactDisabledReason(dealer.contactDisabledReason);
  const canContact = dealer.canContact;
  const whatsappHref = canContact
    ? buildDealerWhatsappUrl({
        dealer,
        listing: options.listing,
        message: options.message,
        detailUrl: options.detailUrl,
      })
    : null;
  const phoneHref = canContact ? buildDealerPhoneHref(dealer) : null;

  return {
    dealerId: dealer.id,
    dealerName: dealer.displayName || dealer.name,
    dealerLogoUrl: dealer.logoUrl,
    locationLabel: dealer.locationLabel || DEALER_PROFILE_FALLBACK_COPY.location,
    verificationLabel: verificationLabel(dealer.verificationStatus),
    whatsapp: dealer.whatsapp,
    phone: dealer.phone,
    profileHref: dealer.profileHref,
    canContact,
    contactDisabledReason: legacyReason || dealer.contactDisabledReason,
    contactDisabledLabel: getContactDisabledLabel(dealer.contactDisabledReason),
    primaryCtaLabel: canContact
      ? DEALER_PROFILE_CONTACT_COPY_ES.whatsapp
      : DEALER_PROFILE_CONTACT_COPY_ES.unavailable,
    secondaryCtaLabel: phoneHref ? DEALER_PROFILE_CONTACT_COPY_ES.phone : null,
    whatsappHref,
    phoneHref,
    trustNote: DEALER_PROFILE_TRUST_NOTE_ES,
    profileCtaLabel: DEALER_PROFILE_CONTACT_COPY_ES.viewProfile,
  };
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|object|null|undefined} dealerInput
 * @param {object[]} [listings]
 */
export function toDealerInventorySummary(dealerInput, listings = []) {
  const dealer = resolveDealerProfile(dealerInput);

  let activeInventoryCount = null;
  let availableInventoryCount = null;
  let soldInventoryCount = null;

  if (Array.isArray(listings) && listings.length > 0) {
    const eligible = getPublicDealerInventoryListings(dealer, listings);
    activeInventoryCount = eligible.length;
    availableInventoryCount = eligible.length;
    soldInventoryCount = listings.filter(
      (item) =>
        (item.dealerId === dealer.id || item.dealerSlug === dealer.slug) &&
        (item.status === "sold" || item.availabilityStatus === "sold")
    ).length;
  } else if (dealer.activeInventoryCount != null) {
    activeInventoryCount = dealer.activeInventoryCount;
    availableInventoryCount = dealer.availableInventoryCount ?? dealer.activeInventoryCount;
    soldInventoryCount = dealer.soldInventoryCount ?? null;
  }

  const inventoryCount = activeInventoryCount;
  const hasActiveInventory = (activeInventoryCount ?? 0) > 0;
  const lastUpdatedLabel = formatDealerLastInventoryLabel(dealer.lastInventoryUpdatedAt);

  return {
    inventoryCount,
    activeInventoryCount,
    availableInventoryCount,
    soldInventoryCount,
    inventoryCountLabel: formatDealerInventoryAvailabilityLabel(activeInventoryCount),
    inventoryAvailabilityLabel: formatDealerInventoryAvailabilityLabel(activeInventoryCount),
    lastInventoryUpdatedLabel: dealer.lastInventoryUpdatedAt || null,
    lastUpdatedLabel,
    hasActiveInventory,
  };
}

/**
 * Dealer profile SEO view model (Dealer Profile M11).
 * @param {import('./dealerProfileTypes').DealerProfile|object|null|undefined} dealerInput
 * @param {{ slug?: string, usePublicRoute?: boolean, listings?: object[], activeInventoryCount?: number|null, publicState?: object|null, inventorySummary?: object|null }} [options]
 */
export function toDealerSeoViewModel(dealerInput, options = {}) {
  const dealer = dealerInput ? normalizeDealerProfile(dealerInput, options) : null;
  const publicState =
    options.publicState ||
    (dealer
      ? getDealerProfilePublicState(dealer, {
          listings: options.listings,
          activeInventoryCount: options.activeInventoryCount,
        })
      : null);
  const inventorySummary =
    options.inventorySummary ||
    (dealer && options.activeInventoryCount != null
      ? { activeInventoryCount: options.activeInventoryCount }
      : null);

  if (!dealer || !publicState?.canShowProfile) {
    return getDealerProfileUnavailableMetadata(publicState);
  }

  return buildDealerProfileSeo(dealer, publicState, inventorySummary, options);
}

/**
 * Analytics payload for dealer profile events (Dealer Profile M13).
 * Excludes phone, WhatsApp, email, and raw address.
 */
export function buildDealerAnalyticsPayload(dealerInput, extra = {}) {
  const dealer = normalizeDealerProfile(dealerInput, { includeRawSource: false });
  const publicState = getDealerProfilePublicState(dealer, {
    listings: extra.listings,
    activeInventoryCount: extra.activeInventoryCount,
  });

  const activeInventoryCount =
    extra.activeInventoryCount ??
    dealer.activeInventoryCount ??
    dealer.inventoryCount ??
    null;

  return {
    dealerId: dealer.id,
    dealerSlug: dealer.slug,
    dealerName: dealer.displayName || dealer.name,
    dealerVerified: dealer.isVerified,
    dealerVerificationStatus: dealer.verificationStatus,
    dealerPublicStatus: dealer.publicStatus,
    dealerPublicState: publicState.state,
    canShowProfile: publicState.canShowProfile,
    canShowInventory: publicState.canShowInventory,
    canContactDealer: publicState.canContactDealer,
    city: dealer.city,
    department: dealer.department,
    inventoryCount: activeInventoryCount,
    activeInventoryCount,
    sourcePage: extra.sourcePage || null,
    sourceComponent: extra.sourceComponent || null,
    sourceAction: extra.sourceAction || null,
  };
}
